import React, { useCallback, useEffect, useState } from 'react';
import Icon from './Icon';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from './Toast';
import {
  StripeMode,
  StripeSettings,
  StripeWebhookEndpoint,
  StripeWebhookStatus,
  getStripeWebhook,
  removeStripeWebhook,
  setupStripeWebhook,
} from '../api/stripeSettings';
import { extractApiError } from '../api/legalServices';

interface StripeWebhookSetupProps {
  mode: StripeMode;
  /** Masked saved secret key for this mode ('' = none). Refetches when it changes. */
  savedSecretKey: string;
  /** Why creating is blocked right now (e.g. unsaved key edits), or null. */
  blockedReason: string | null;
  /** The webhook secret was saved — hand the fresh settings to the page. */
  onSettingsChanged: (s: StripeSettings) => void;
}

type Tone = 'green' | 'yellow' | 'red' | 'gray';

const TONES: Record<Tone, { color: string; bg: string }> = {
  green: { color: 'var(--green)', bg: 'var(--green-bg)' },
  yellow: { color: 'var(--yellow)', bg: 'var(--yellow-bg)' },
  red: { color: 'var(--red)', bg: 'var(--red-bg)' },
  gray: { color: 'var(--g600)', bg: 'var(--g100)' },
};

const Pill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap"
    style={{ background: TONES[tone].bg, color: TONES[tone].color }}
  >
    {children}
  </span>
);

/** One-line verdict on the endpoint for this server's address. */
const verdict = (
  current: StripeWebhookEndpoint | undefined,
  others: StripeWebhookEndpoint[],
  label: string
): { tone: Tone; badge: string; text: string } => {
  if (!current && others.some((e) => e.status === 'enabled')) {
    return {
      tone: 'yellow',
      badge: 'Old address',
      text: `Stripe is sending ${label} events to an older address for this app (listed below). Create the webhook for this address, then remove the old one.`,
    };
  }
  if (!current) {
    return {
      tone: 'gray',
      badge: 'Not set up',
      text: `Stripe isn't sending ${label} events to this server yet. Subscription renewals, failed payments and cancellations won't sync until it does.`,
    };
  }
  if (current.status === 'disabled') {
    return {
      tone: 'red',
      badge: 'Disabled',
      text: 'Stripe has disabled this webhook (usually after repeated delivery failures). Recreate it to turn it back on.',
    };
  }
  if (!current.secretSaved) {
    return {
      tone: 'yellow',
      badge: 'Check secret',
      text: "This webhook was created in the Stripe dashboard, so this page can't confirm the saved signing secret belongs to it. Recreate it here to be sure.",
    };
  }
  if (current.missingEvents.length) {
    return {
      tone: 'yellow',
      badge: 'Missing events',
      text: `Not subscribed to: ${current.missingEvents.join(', ')}. Recreate it to fix.`,
    };
  }
  return {
    tone: 'green',
    badge: 'Connected',
    text: 'Stripe sends events to this server and the signing secret is saved.',
  };
};

const StripeWebhookSetup: React.FC<StripeWebhookSetupProps> = ({
  mode,
  savedSecretKey,
  blockedReason,
  onSettingsChanged,
}) => {
  const { toast } = useToast();
  const label = mode === 'live' ? 'live' : 'test';
  const [status, setStatus] = useState<StripeWebhookStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [confirmRecreate, setConfirmRecreate] = useState<boolean>(false);
  const [removing, setRemoving] = useState<StripeWebhookEndpoint | null>(null);

  const load = useCallback(async () => {
    if (!savedSecretKey) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setStatus(await getStripeWebhook(mode));
    } catch (err) {
      setError(extractApiError(err, 'Failed to check the webhook'));
    } finally {
      setLoading(false);
    }
  }, [mode, savedSecretKey]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await setupStripeWebhook(mode);
      setStatus(res.webhook);
      onSettingsChanged(res.stripe);
      toast(res.message, 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to create the webhook'), 'error');
    } finally {
      setBusy(false);
      setConfirmRecreate(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      setStatus(await removeStripeWebhook(mode, removing.id));
      toast('Webhook removed from Stripe', 'success');
    } catch (err) {
      toast(extractApiError(err, 'Failed to remove the webhook'), 'error');
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };

  const current = status?.endpoints.find((e) => e.isCurrent);
  const others = status?.endpoints.filter((e) => !e.isCurrent) ?? [];
  const v = verdict(current, others, label);
  // Once this address's webhook owns the saved secret, events at old
  // addresses fail signature checks — they're just noise.
  const currentOwnsSecret = !!current && current.secretSaved;
  const isLocal = !!status && /\/\/(localhost|127\.0\.0\.1|\[::1\])[:/]/.test(status.url);
  const disabled = busy || !!blockedReason;

  return (
    <div className="rounded-2xl border border-g200 p-4 sm:p-5 mt-1 mb-2">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Icon name="link" size={16} color="var(--brand)" />
          <span className="text-[14px] font-bold text-ink">Webhook</span>
        </div>
        {savedSecretKey && status && <Pill tone={v.tone}>{v.badge}</Pill>}
      </div>

      {!savedSecretKey ? (
        <p className="text-[13px] text-g500">
          Save the {label} secret key above, then create the webhook here in one click. The
          signing secret is filled in for you.
        </p>
      ) : loading && !status ? (
        <p className="text-[13px] text-g500">Checking Stripe...</p>
      ) : error ? (
        <div className="text-[13px]" style={{ color: 'var(--red)' }}>
          <div className="mb-2">{error}</div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      ) : status ? (
        <>
          <p className="text-[13px] text-g600 leading-relaxed mb-3">{v.text}</p>

          <div className="rounded-xl bg-g50 border border-g100 px-3.5 py-2.5 mb-3">
            <div className="text-[11.5px] font-semibold text-g500 mb-0.5">Stripe sends events to</div>
            <div className="font-mono text-[12.5px] text-ink break-all">{status.url}</div>
            <details className="mt-1.5">
              <summary className="text-[12px] text-g500 cursor-pointer select-none">
                Listens for {status.events.length} events
              </summary>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {status.events.map((e) => (
                  <li key={e} className="font-mono text-[11.5px] px-2 py-0.5 rounded-md bg-white border border-g200 text-g700">
                    {e}
                  </li>
                ))}
              </ul>
            </details>
          </div>

          {isLocal && (
            <div
              className="flex items-start gap-2 px-3.5 py-2.5 rounded-xl text-[12.5px] mb-3"
              style={{ background: 'var(--yellow-bg)', color: 'var(--yellow)' }}
            >
              <Icon name="info" size={15} color="var(--yellow)" />
              <span>
                Stripe can't reach a localhost address. Create the webhook from the live admin
                panel instead.
              </span>
            </div>
          )}

          {others.length > 0 && (
            <div className="mb-3">
              <div className="text-[12px] font-semibold text-g600 mb-1.5">
                Other webhooks for this app in Stripe
              </div>
              <ul className="flex flex-col gap-1.5">
                {others.map((ep) => (
                  <li
                    key={ep.id}
                    className="flex items-center gap-2.5 rounded-xl border border-g100 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[12px] text-ink break-all">{ep.url}</div>
                      <div className="text-[11.5px] text-g500">
                        {ep.status === 'enabled' ? 'Enabled' : 'Disabled'} ·{' '}
                        {currentOwnsSecret
                          ? 'old address — no longer matches the saved signing secret, safe to remove'
                          : 'different address — remove it once the webhook above is set up'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm flex-shrink-0"
                      disabled={busy}
                      onClick={() => setRemoving(ep)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              className={current ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
              disabled={disabled}
              style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={() => (current ? setConfirmRecreate(true) : create())}
            >
              <Icon name="link" size={13} color={current ? undefined : '#fff'} />
              {busy && !removing ? 'Working...' : current ? 'Recreate webhook' : 'Create webhook'}
            </button>
            {blockedReason && <span className="text-[12px] text-g500">{blockedReason}</span>}
          </div>
        </>
      ) : null}

      <ConfirmDialog
        open={confirmRecreate}
        title={`Recreate the ${label} webhook?`}
        variant="primary"
        confirmLabel="Recreate webhook"
        busy={busy}
        message={
          <>
            This deletes the existing webhook for{' '}
            <span className="font-mono text-[12.5px] break-all" style={{ color: 'var(--ink)' }}>
              {status?.url}
            </span>{' '}
            in Stripe and creates a new one. Its new signing secret is saved here automatically.
          </>
        }
        onConfirm={create}
        onCancel={() => setConfirmRecreate(false)}
      />
      <ConfirmDialog
        open={removing !== null}
        title="Remove this webhook from Stripe?"
        variant="danger"
        confirmLabel="Remove webhook"
        busy={busy}
        message={
          <>
            Stripe will stop sending events to{' '}
            <span className="font-mono text-[12.5px] break-all" style={{ color: 'var(--ink)' }}>
              {removing?.url}
            </span>
            . This can't be undone.
            {!currentOwnsSecret && removing?.status === 'enabled' && (
              <>
                {' '}
                <strong style={{ color: 'var(--red)' }}>
                  The webhook for this address isn't set up yet, so subscription updates will stop
                  syncing until you create it.
                </strong>
              </>
            )}
          </>
        }
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

export default StripeWebhookSetup;
